/**
 * <announcement-bar data-interval>
 *   Slider mode: one message at a time ([data-announcement-slide]) with
 *   previous/next buttons and autoplay that pauses on hover, focus, a
 *   hidden tab and reduced motion. Using the arrows stops autoplay.
 */
if (!customElements.get('announcement-bar')) {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-announcement-slide]'));
      if (this.slides.length < 2) return;
      this.active = Math.max(0, this.slides.findIndex((slide) => !slide.hidden));
      this.interval = Number(this.dataset.interval) || 5000;
      this.autoplay = true;
      this.holds = new Set();

      this.querySelector('[data-announcement-prev]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.step(1));
      this.addEventListener('mouseenter', () => this.hold('hover'));
      this.addEventListener('mouseleave', () => this.release('hover'));
      this.addEventListener('focusin', () => this.hold('focus'));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.release('focus');
      });
      this.onVisibility = () => (document.hidden ? this.hold('tab') : this.release('tab'));
      document.addEventListener('visibilitychange', this.onVisibility);
      /* Theme editor: selecting a message shows it and holds the slider. */
      this.onBlockSelect = (event) => {
        const index = this.slides.indexOf(event.target.closest?.('[data-announcement-slide]'));
        if (index < 0) return;
        this.show(index);
        this.hold('editor');
      };
      this.onBlockDeselect = () => this.release('editor');
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      this.schedule();
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      if (!this.onVisibility) return;
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    show(index, animate = false) {
      const count = this.slides.length;
      this.active = (index + count) % count;
      this.slides.forEach((slide, i) => {
        const on = i === this.active;
        slide.hidden = !on;
        slide.classList.toggle('is-active', on);
        slide.classList.toggle('is-entering', on && animate && !reducedMotion.matches);
      });
    }

    step(direction) {
      this.autoplay = false;
      window.clearTimeout(this.timer);
      this.show(this.active + direction, true);
    }

    hold(reason) {
      this.holds.add(reason);
      window.clearTimeout(this.timer);
    }

    release(reason) {
      this.holds.delete(reason);
      this.schedule();
    }

    schedule() {
      window.clearTimeout(this.timer);
      if (!this.autoplay || this.holds.size || reducedMotion.matches) return;
      this.timer = window.setTimeout(() => {
        this.show(this.active + 1, true);
        this.schedule();
      }, this.interval);
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
